/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { isNull } from "lodash";
import isEmpty from "lodash/fp/isEmpty";

import { ReportTypeEnum } from "~/features/reportTracker/constants";
import { ReportDetails } from "~/features/reportTracker/types";
import transformToGeoCountry from "~/features/statistics/transformers/transformToGeoCountry";
import transformToGeoTotals from "~/features/statistics/transformers/transformToGeoTotals";
import transformToPlatformStatsPlatform from "~/features/statistics/transformers/transformToPlatformStatsPlatform";
import transformToTotalStatsSite from "~/features/statistics/transformers/transformToTotalStatsSite";
import transformToTotalStatsTotals from "~/features/statistics/transformers/transformToTotalStatsTotals";
import transformToUpsellStatsSite from "~/features/statistics/transformers/transformToUpsellStatsSite";
import transformToUserSpendingUser from "~/features/statistics/transformers/transformToUserSpendingUser";
import transformToUserValueStatsSite from "~/features/statistics/transformers/transformToUserValueSite";
import {
  PostGeoResponseData,
  PostPastPayoutsResponseData,
  PostPlatformStatsResponseData,
  PostTotalStatsResponseData,
  PostUpsellStatsResponseData,
  PostUserSpendingResponseData,
  PostUserValueResponseData,
  PostVideoSalesResponseData,
} from "~/features/statistics/types";

const mapReportData = (result: ReportDetails) => {
  switch (result.params.reportType as ReportTypeEnum) {
    case ReportTypeEnum.totalStats:
      return {
        cacheKey: result.cacheKey,
        status: result.status,
        params: result.params,
        data: {
          totals:
            !isNull(result.data) && !isEmpty((result as unknown as PostTotalStatsResponseData).data.totals)
              ? transformToTotalStatsTotals((result as unknown as PostTotalStatsResponseData).data.totals)
              : [],
          sites:
            !isNull(result.data) && !isEmpty((result as unknown as PostTotalStatsResponseData).data.body)
              ? (result as unknown as PostTotalStatsResponseData).data.body.map(transformToTotalStatsSite)
              : [],
        },
      } as unknown as ReportDetails;
    case ReportTypeEnum.platformStats:
      return {
        cacheKey: result.cacheKey,
        status: result.status,
        params: result.params,
        data: {
          totals:
            !isNull(result.data) && !isEmpty((result.data as PostPlatformStatsResponseData).data.totals)
              ? (result.data as PostPlatformStatsResponseData).data.totals
              : [],
          platforms:
            !isNull(result.data) && !isEmpty((result.data as PostPlatformStatsResponseData).data.body)
              ? (result.data as PostPlatformStatsResponseData).data.body.map(transformToPlatformStatsPlatform)
              : [],
        },
      } as unknown as ReportDetails;
    case ReportTypeEnum.userValue:
      return {
        cacheKey: result.cacheKey,
        status: result.status,
        params: result.params,
        data: {
          totals:
            !isNull(result.data) && !isEmpty((result as unknown as PostUserValueResponseData).data.totals)
              ? (result as unknown as PostUserValueResponseData).data.totals
              : [],
          sites:
            !isNull(result.data) && !isEmpty((result as unknown as PostUserValueResponseData).data.body)
              ? (result as unknown as PostUserValueResponseData).data.body.map(transformToUserValueStatsSite)
              : [],
        },
      } as unknown as ReportDetails;
    case ReportTypeEnum.geo:
      return {
        cacheKey: result.cacheKey,
        status: result.status,
        params: result.params,
        data: {
          totals:
            !isNull(result.data) && !isEmpty((result as unknown as PostGeoResponseData).data.totals)
              ? transformToGeoTotals((result as unknown as PostGeoResponseData).data.totals)
              : [],
          countries:
            !isNull(result.data) && !isEmpty((result as unknown as PostGeoResponseData).data.body)
              ? (result as unknown as PostGeoResponseData).data.body.map(transformToGeoCountry)
              : [],
        },
      } as unknown as ReportDetails;
    case ReportTypeEnum.upsellStats:
      return {
        cacheKey: result.cacheKey,
        status: result.status,
        params: result.params,
        data: {
          totals:
            !isNull(result.data) && !isEmpty((result as unknown as PostUpsellStatsResponseData).data.totals)
              ? (result as unknown as PostUpsellStatsResponseData).data.totals
              : [],
          sites:
            !isNull(result.data) && !isEmpty((result as unknown as PostUpsellStatsResponseData).data.body)
              ? (result as unknown as PostUpsellStatsResponseData).data.body.map(transformToUpsellStatsSite)
              : [],
        },
      } as unknown as ReportDetails;
    case ReportTypeEnum.pastPayouts:
      return {
        cacheKey: result.cacheKey,
        status: result.status,
        params: result.params,
        data: {
          payments:
            !isNull(result.data) && !isEmpty((result as unknown as PostPastPayoutsResponseData).data)
              ? (result as unknown as PostPastPayoutsResponseData).data
              : [],
        },
      } as unknown as ReportDetails;
    case ReportTypeEnum.userSpending:
      return {
        cacheKey: result.cacheKey,
        status: result.status,
        params: result.params,
        data: {
          users:
            !isNull(result.data) && !isEmpty((result as unknown as PostUserSpendingResponseData).data.body)
              ? (result as unknown as PostUserSpendingResponseData).data.body.map(transformToUserSpendingUser)
              : [],
        },
      } as unknown as ReportDetails;
    case ReportTypeEnum.videoSales:
      return {
        cacheKey: result.cacheKey,
        status: result.status,
        params: result.params,
        data: {
          totals:
            !isNull(result.data) && !isEmpty((result.data as PostVideoSalesResponseData).data.totals)
              ? (result.data as PostVideoSalesResponseData).data.totals
              : [],
          sites:
            !isNull(result.data) && !isEmpty((result.data as PostVideoSalesResponseData).data.body)
              ? (result.data as PostVideoSalesResponseData).data.body
              : [],
        },
      } as unknown as ReportDetails;
    default:
      return result;
  }
};

export default mapReportData;
